import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  MenuItem,
  Chip,
  Snackbar
} from "@mui/material";
import {
  Business,
  Work,
  People,
  Visibility,
  TrendingUp,
  Add,
  Edit,
  Delete,
  Publish,
  CheckCircle,
  Warning,
  Close
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../config/api";
import useSEO from "../hooks/useSEO";

const EmployerDashboard = () => {
  useSEO({ title: 'Кабинет работодателя | JobMD' });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [employer, setEmployer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [createJobDialog, setCreateJobDialog] = useState(false);
  const [editProfileDialog, setEditProfileDialog] = useState(false);
  
  const [newJob, setNewJob] = useState({
    title: "",
    description: "",
    requirements: "",
    salary_from: "",
    salary_to: "",
    salary_currency: "USD",
    employment_type: "full-time",
    experience_level: "middle",
    location_city: "",
    contact_email: "",
    contact_phone: "",
    status: "draft"
  });
  
  const [profileData, setProfileData] = useState({
    company_name: "",
    company_description: "",
    industry: "",
    company_size: "1-10",
    website: "",
    phone: "",
    email: "",
    address: ""
  });

  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (userId) {
      loadEmployerData();
    } else {
      setError("Требуется авторизация");
      setLoading(false);
    }
  }, [userId]);

  const loadEmployerData = async () => {
    setLoading(true);
    try {
      // Загружаем профиль работодателя
      const employerResponse = await fetch(`${apiUrl("employers.php")}?action=my-profile&userId=${userId}`);
      const employerData = await employerResponse.json();
      
      if (employerData.success) {
        setEmployer(employerData.employer);
        setProfileData({
          company_name: employerData.employer.company_name || "",
          company_description: employerData.employer.company_description || "",
          industry: employerData.employer.industry || "",
          company_size: employerData.employer.company_size || "1-10",
          website: employerData.employer.company_website || "",
          phone: employerData.employer.company_phone || "",
          email: employerData.employer.company_email || "",
          address: employerData.employer.company_address || ""
        });
      } else {
        setEmployer(null);
      }

      // Загружаем вакансии
      const jobsResponse = await fetch(`${apiUrl("employer_jobs.php")}?action=my-jobs&userId=${userId}`);
      const jobsData = await jobsResponse.json();
      if (jobsData.success) {
        setJobs(jobsData.jobs);
      }

    } catch (err) {
      setError("Ошибка загрузки данных: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async () => {
    // Проверяем, есть ли профиль работодателя
    if (!employer) {
      setError("Сначала создайте профиль компании");
      setCreateJobDialog(false);
      setEditProfileDialog(true);
      return;
    }
    
    try {
      const response = await fetch(`${apiUrl("employer_jobs.php")}?action=create&userId=${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newJob)
      });

      const data = await response.json();
      if (data.success) {
        setCreateJobDialog(false);
        setNewJob({
          title: "",
          description: "",
          requirements: "",
          salary_from: "",
          salary_to: "",
          salary_currency: "USD",
          employment_type: "full-time",
          experience_level: "middle",
          location_city: "",
          contact_email: "",
          contact_phone: "",
          status: "draft"
        });
        setSuccess("Вакансия успешно создана!");
        loadEmployerData();
      } else {
        setError("Ошибка: " + data.message);
      }
    } catch (err) {
      setError("Ошибка: " + err.message);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const response = await fetch(`${apiUrl("employers.php")}?action=save-profile&userId=${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profileData)
      });

      const data = await response.json();
      if (data.success) {
        setEditProfileDialog(false);
        setSuccess("Профиль компании сохранен!");
        loadEmployerData();
      } else {
        setError("Ошибка: " + data.message);
      }
    } catch (err) {
      setError("Ошибка: " + err.message);
    }
  };

  const handleSubmitForReview = async (jobId) => {
    try {
      const response = await fetch(`${apiUrl("employer_jobs.php")}?action=submit-for-review&userId=${userId}&jobId=${jobId}`);
      const data = await response.json();
      if (data.success) {
        setSuccess("Вакансия отправлена на проверку администратору!");
        loadEmployerData();
      } else {
        setError("Ошибка: " + data.message);
      }
    } catch (err) {
      setError("Ошибка: " + err.message);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm("Удалить вакансию?")) {
      try {
        const response = await fetch(`${apiUrl("employer_jobs.php")}?action=delete&userId=${userId}&jobId=${jobId}`);
        const data = await response.json();
        if (data.success) {
          setSuccess("Вакансия удалена!");
          loadEmployerData();
        } else {
          setError("Ошибка: " + data.message);
        }
      } catch (err) {
        setError("Ошибка: " + err.message);
      }
    }
  };

  const getStatusColor = (status, verificationStatus) => {
    // Используем verification_status вместо status
    switch (verificationStatus) {
      case 'published': return 'success';
      case 'pending_review': return 'warning';
      case 'rejected': return 'error';
      case 'draft': return 'default';
      default: return 'info';
    }
  };

  const getStatusText = (status, verificationStatus) => {
    // Используем verification_status вместо status
    switch (verificationStatus) {
      case 'published': return 'Опубликована';
      case 'pending_review': return 'На проверке';
      case 'rejected': return 'Отклонена';
      case 'draft': return 'Черновик';
      default: return verificationStatus || status;
    }
  };

  const getCompanyStatus = () => {
    if (!employer) return null;
    
    switch (employer.company_status) {
      case 'active':
        return {
          text: 'Активна',
          color: 'success',
          icon: <CheckCircle />
        };
      case 'suspended':
        return {
          text: 'Заблокирована',
          color: 'error',
          icon: <Close />
        };
      default:
        return {
          text: 'Неизвестно',
          color: 'default',
          icon: <Warning />
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("ru-RU");
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const companyStatus = getCompanyStatus();

  return (
    <Box sx={{ maxWidth: "1400px", mx: "auto", p: { xs: 2, md: 3 } }}>
      {/* Уведомления */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError("")} message={error} />
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess("")} message={success} />

      {/* Заголовок */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Кабинет работодателя 🏢
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {employer ? employer.company_name : "Создайте профиль компании"}
          </Typography>
          
          {employer && companyStatus && (
            <Chip
              icon={companyStatus.icon}
              label={companyStatus.text}
              color={companyStatus.color}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        
        <Stack direction="row" spacing={2}>
          {!employer ? (
            <Button variant="contained" startIcon={<Business />} onClick={() => setEditProfileDialog(true)}>
              Создать профиль
            </Button>
          ) : (
            <>
              <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditProfileDialog(true)}>
                Редактировать
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={() => {
                if (!employer) {
                  setError("Сначала создайте профиль компании");
                  setEditProfileDialog(true);
                } else {
                  setCreateJobDialog(true);
                }
              }}>
                Новая вакансия
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* Вкладки */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Мои вакансии" />
          <Tab label="Профиль" />
        </Tabs>
      </Box>

      {/* Вкладка: Мои вакансии */}
      {activeTab === 0 && (
        <Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Мои вакансии
          </Typography>
          
          {jobs.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: "center", py: 6 }}>
                <Work sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {employer ? 'У вас пока нет вакансий' : 'Сначала создайте профиль компании'}
                </Typography>
                {employer ? (
                  <Button variant="contained" startIcon={<Add />} onClick={() => setCreateJobDialog(true)}>
                    Создать первую вакансию
                  </Button>
                ) : (
                  <Button variant="contained" startIcon={<Business />} onClick={() => setEditProfileDialog(true)}>
                    Создать профиль компании
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Название</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Просмотры</TableCell>
                    <TableCell>Отклики</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <Typography fontWeight="medium">{job.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {job.location_city}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusText(job.status, job.verification_status)} 
                          color={getStatusColor(job.status, job.verification_status)} 
                          size="small" 
                        />
                        {job.reject_reason && (
                          <Typography variant="caption" color="error" display="block">
                            Причина: {job.reject_reason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{job.views_count || 0}</TableCell>
                      <TableCell>{job.applications_count || 0}</TableCell>
                      <TableCell>{formatDate(job.created_at)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {job.verification_status === 'draft' && (
                            <Button 
                              size="small" 
                              variant="outlined" 
                              startIcon={<Publish />} 
                              onClick={() => handleSubmitForReview(job.id)}
                            >
                              Отправить на проверку
                            </Button>
                          )}
                          {job.verification_status === 'rejected' && (
                            <Button 
                              size="small" 
                              variant="outlined" 
                              startIcon={<Edit />} 
                              onClick={() => {
                                // Можно добавить редактирование и повторную отправку
                                alert('Отредактируйте вакансию и отправьте снова');
                              }}
                            >
                              Исправить
                            </Button>
                          )}
                          <IconButton size="small" color="error" onClick={() => handleDeleteJob(job.id)}>
                            <Delete />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Вкладка: Профиль */}
      {activeTab === 1 && (
        <Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Профиль компании
          </Typography>
          
          <Card>
            <CardContent>
              {employer ? (
                <Stack spacing={3}>
                  <Typography variant="h6">{employer.company_name}</Typography>
                  <Typography>{employer.company_description || "Нет описания"}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Отрасль: {employer.industry || "Не указана"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Сайт: {employer.company_website || "Не указан"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Телефон: {employer.company_phone || "Не указан"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Email: {employer.company_email || "Не указан"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Адрес: {employer.company_address || "Не указан"}
                  </Typography>
                  <Button variant="outlined" onClick={() => setEditProfileDialog(true)}>
                    Редактировать профиль
                  </Button>
                </Stack>
              ) : (
                <Box textAlign="center" py={4}>
                  <Business sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Профиль компании не создан
                  </Typography>
                  <Button variant="contained" onClick={() => setEditProfileDialog(true)}>
                    Создать профиль
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Диалог создания вакансии */}
      <Dialog open={createJobDialog} onClose={() => setCreateJobDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Создать новую вакансию</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField label="Название вакансии" value={newJob.title} onChange={(e) => setNewJob({...newJob, title: e.target.value})} fullWidth required />
            <TextField label="Описание" value={newJob.description} onChange={(e) => setNewJob({...newJob, description: e.target.value})} fullWidth multiline rows={4} required />
            <TextField label="Требования" value={newJob.requirements} onChange={(e) => setNewJob({...newJob, requirements: e.target.value})} fullWidth multiline rows={3} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Город" value={newJob.location_city} onChange={(e) => setNewJob({...newJob, location_city: e.target.value})} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Тип занятости</InputLabel>
                  <Select value={newJob.employment_type} onChange={(e) => setNewJob({...newJob, employment_type: e.target.value})} label="Тип занятости">
                    <MenuItem value="full-time">Полный день</MenuItem>
                    <MenuItem value="part-time">Частичная занятость</MenuItem>
                    <MenuItem value="contract">Контракт</MenuItem>
                    <MenuItem value="internship">Стажировка</MenuItem>
                    <MenuItem value="remote">Удаленная работа</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Минимальная зарплата" value={newJob.salary_from} onChange={(e) => setNewJob({...newJob, salary_from: e.target.value})} type="number" fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Максимальная зарплата" value={newJob.salary_to} onChange={(e) => setNewJob({...newJob, salary_to: e.target.value})} type="number" fullWidth />
              </Grid>
            </Grid>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Валюта</InputLabel>
                  <Select value={newJob.salary_currency} onChange={(e) => setNewJob({...newJob, salary_currency: e.target.value})} label="Валюта">
                    <MenuItem value="USD">USD ($)</MenuItem>
                    <MenuItem value="EUR">EUR (€)</MenuItem>
                    <MenuItem value="RUB">RUB (₽)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Уровень опыта</InputLabel>
                  <Select value={newJob.experience_level} onChange={(e) => setNewJob({...newJob, experience_level: e.target.value})} label="Уровень опыта">
                    <MenuItem value="intern">Стажер</MenuItem>
                    <MenuItem value="junior">Junior</MenuItem>
                    <MenuItem value="middle">Middle</MenuItem>
                    <MenuItem value="senior">Senior</MenuItem>
                    <MenuItem value="lead">Lead</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Email для откликов" value={newJob.contact_email} onChange={(e) => setNewJob({...newJob, contact_email: e.target.value})} type="email" fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Телефон для связи" value={newJob.contact_phone} onChange={(e) => setNewJob({...newJob, contact_phone: e.target.value})} fullWidth />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateJobDialog(false)}>Отмена</Button>
          <Button onClick={handleCreateJob} variant="contained">Создать вакансию</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог редактирования профиля */}
      <Dialog open={editProfileDialog} onClose={() => setEditProfileDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{employer ? 'Редактировать профиль компании' : 'Создать профиль компании'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField label="Название компании" value={profileData.company_name} onChange={(e) => setProfileData({...profileData, company_name: e.target.value})} fullWidth required />
            <TextField label="Описание компании" value={profileData.company_description} onChange={(e) => setProfileData({...profileData, company_description: e.target.value})} fullWidth multiline rows={4} />
            <TextField label="Отрасль" value={profileData.industry} onChange={(e) => setProfileData({...profileData, industry: e.target.value})} fullWidth />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Веб-сайт" value={profileData.website} onChange={(e) => setProfileData({...profileData, website: e.target.value})} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Телефон" value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} fullWidth />
              </Grid>
            </Grid>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Email" value={profileData.email} onChange={(e) => setProfileData({...profileData, email: e.target.value})} type="email" fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Размер компании</InputLabel>
                  <Select value={profileData.company_size} onChange={(e) => setProfileData({...profileData, company_size: e.target.value})} label="Размер компании">
                    <MenuItem value="1-10">1-10 сотрудников</MenuItem>
                    <MenuItem value="11-50">11-50 сотрудников</MenuItem>
                    <MenuItem value="51-200">51-200 сотрудников</MenuItem>
                    <MenuItem value="201-500">201-500 сотрудников</MenuItem>
                    <MenuItem value="501-1000">501-1000 сотрудников</MenuItem>
                    <MenuItem value="1000+">1000+ сотрудников</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <TextField label="Адрес" value={profileData.address} onChange={(e) => setProfileData({...profileData, address: e.target.value})} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProfileDialog(false)}>Отмена</Button>
          <Button onClick={handleSaveProfile} variant="contained">Сохранить профиль</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployerDashboard;
